import React, { useEffect } from 'react';

import { useAppDispatch, useAppSelector } from '~redux/hooks';
import combinedActions from '~redux/combinedActions';

import Table from '~components/molecules/Table';
import ActionButton from '~components/atoms/ActionButton';

import UtilFunctions from '~utils/functions';
import { CustomerAccFundTypeEnum, CustomerAccTypeEnum, CustomerAccStatusEnum, CustomerIdTypeEnum, CustomerRaceEnum, ModuleEnum, PermissionsEnum } from '~utils/enums';

import * as Styles from './_customerManagementTable.module.scss';

const CustomerManagementTable = () => {
    const { Misc: { renderCustomerStatusChip, dateTimeFormat, sanitizeReferralCode } } = UtilFunctions;

    const customerListingIndex = useAppSelector(state => state.customer.customerListing.index);

    useEffect(() => {
        dispatch(combinedActions.customerGetCustomerListingAttempt());
    }, [customerListingIndex]);

    const isLoading = useAppSelector(state => state.customer.loadings.getCustomerListing);
    const customerListing = useAppSelector(state => state.customer.customerListing.data);
    const userAccess = useAppSelector(state => state.auth.userDetails.access);
    const allBranches = useAppSelector(state => state.branch.allBranches);

    const dispatch = useAppDispatch();

    const setCustomerDetailsModalIsOpen = (isOpen: boolean) => dispatch(combinedActions.customerSetCustomerDetailsModalIsOpen(isOpen));
    const setDeleteCustomerModalIsOpen = (isOpen: boolean) => dispatch(combinedActions.customerSetDeleteCustomerModalIsOpen(isOpen));
    const setIsEditing = (isEditing: boolean) => dispatch(combinedActions.customerSetCustomerDetailsModalIsEditing(isEditing));
    const getCustomerDetails = (customerId: number) => dispatch(combinedActions.customerGetCustomerDetailsAttempt({ customerId }));
    const resetCustomerDetails = () => dispatch(combinedActions.customerResetCustomerDetails());

    const hasEditAccess = !!userAccess.find(item => item.permission === PermissionsEnum.Edit)?.modules.find(module => module === ModuleEnum.CustomerManagement);

    const handleViewCustomerDetailsClick = (customerId: number) => {
        setCustomerDetailsModalIsOpen(true);
        getCustomerDetails(customerId);
    };

    const handleEditCustomerClick = (customerId: number) => {
        setIsEditing(true);
        setCustomerDetailsModalIsOpen(true);
        getCustomerDetails(customerId);
    };

    const handleDeleteCustomerClick = (customerId: number) => {
        setIsEditing(false);
        setDeleteCustomerModalIsOpen(true);
        getCustomerDetails(customerId);
    };

    const handleOnClickPaperGoldBalance = (customerId: number) =>{
        dispatch(combinedActions.customerSetCustomerPaperGoldBalanceIsOpen(true));
        getCustomerDetails(customerId);
    };

    const tableHeader = [
        'CUSTOMER NAME',
        'ACCOUNT TYPE',
        'EMAIL',
        'CIF NO.',
        'CONTACT NO.',
        'ID TYPE',
        'ID NO.',
        'BRANCH',
        'BRANCH CODE',
        'NATIONALITY',
        'OCCUPATION',
        'RACE',
        'FUND TYPE',
        'PAPER GOLD BALANCE (G)',
        'GOLD EARMARK BALANCE (G)',
        'GOLD EARMARK END DATE',
        'PAPER SILVER BALANCE (G)',
        'SILVER EARMARK BALANCE (G)',
        'SILVER EARMARK END DATE',
        'REFERRAL CODE',
        'STATUS',
        'ACCOUNT CREATED AT',
        'LAST UPDATED',
        'ACTION',
    ];

    const tableBody = customerListing.map(customer => {
        const {
            customerId,
            name,
            accountType,
            email,
            CIFNo,
            idType,
            idNo,
            contactNo,
            branchName,
            branchId,
            nationality,
            occupation,
            race,
            fundType,
            paperGoldBalance,
            goldEarmarkBalance,
            goldEarmarkEndDate,
            paperSilverBalance,
            silverEarmarkBalance,
            silverEarmarkEndDate,
            refCode,
            status,
            createdAt,
            updatedAt,
        } = customer;

        const branchCode = allBranches.find(branch => branch.branchId === branchId)?.branchCode || '';

        const massagedData = [
            name,
            CustomerAccTypeEnum[accountType] || '-',
            email,
            CIFNo,
            contactNo,
            CustomerIdTypeEnum[idType] || '-',
            idNo,
            branchName,
            branchCode,
            nationality,
            occupation,
            CustomerRaceEnum[race] || '-',
            CustomerAccFundTypeEnum[fundType] || '-',
            paperGoldBalance ? `${paperGoldBalance} g` : '-',
            goldEarmarkBalance ? `${goldEarmarkBalance} g` : '-',
            goldEarmarkEndDate ? dateTimeFormat(goldEarmarkEndDate) : 'N/A',
            paperSilverBalance ? `${paperSilverBalance} g` : '-',
            silverEarmarkBalance ? `${silverEarmarkBalance} g` : '-',
            silverEarmarkEndDate ? dateTimeFormat(silverEarmarkEndDate) : 'N/A',
            refCode ? sanitizeReferralCode(refCode) : '-',
            renderCustomerStatusChip(status, CustomerAccStatusEnum[status]) || '-',
            dateTimeFormat(createdAt),
            updatedAt ? dateTimeFormat(updatedAt) : '-',
        ];

        return [
            ...massagedData,
            (
                <div
                    key={customerId}
                    className={Styles['button-container']}
                >
                    <ActionButton
                        testId='customer-table-view-button'
                        label='View'
                        onClick={() => handleViewCustomerDetailsClick(customerId)}
                    />

                    <>
                        {hasEditAccess
                            && (
                                <ActionButton
                                    testId='customer-table-edit-button'
                                    label='Edit'
                                    onClick={() => handleEditCustomerClick(customerId)}
                                />
                            )
                        }

                        {(hasEditAccess && status !== CustomerAccStatusEnum.Deactivated)
                            && (
                                <ActionButton
                                    testId='customer-table-delete-button'
                                    label='Delete'
                                    onClick={() => handleDeleteCustomerClick(customerId)}
                                    className={Styles.danger}
                                />
                            )
                        }
                    </>
                </div>
            ),
        ];
    });

    return (
        <Table
            isLoading={isLoading}
            header={tableHeader}
            body={tableBody}
        />
    );
};

export default CustomerManagementTable;
